import { Modal } from 'react-bootstrap'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useCurrencyExchangeRate } from 'shared/currency-exchange-rate/api/fetchCurrencyExchangeRate'
import notifyExchangeRateError from 'shared/currency-exchange-rate/components/notifyExchangeRateError'
import LoadingSpinner from '../../../../../components/LoadingSpinner'
import { useFetchGuaranteeCalculation } from '../../api/fetchGuaranteeCalculation'
import GuaranteeCalculationRow from './GuaranteeCalculationRow'
import { EMPTY_GUARANTEE_CALCULATION } from '../../models/types'
import { NctsCountry } from '../../../../../config'
import LoadingBackdrop from '../../../../../components/LoadingBackdrop'

interface GuaranteeCalculationModalProps {
  transitOperationId: number | null
  country: NctsCountry | null
  isOpen: boolean
  onClose: () => void
  onResolve: (value: number) => void
}

function GuaranteeCalculationModal({
  transitOperationId,
  country,
  isOpen,
  onClose,
  onResolve,
}: GuaranteeCalculationModalProps) {
  const { t } = useTranslation()

  const {
    data: exchangeRateData,
    error: exchangeRateError,
    isFetching: isExchangeRateLoading,
  } = useCurrencyExchangeRate(country)

  useEffect(() => {
    if (exchangeRateError) {
      notifyExchangeRateError(exchangeRateError)
    }
  }, [exchangeRateError])

  const isFetchCalculationEnabled = isOpen
    && exchangeRateData != null
    && transitOperationId !== null
    && !isExchangeRateLoading

  const exchangeRate = exchangeRateData ?? 0
  const {
    data,
    isFetching: isGuaranteeCalculationLoading,
  } = useFetchGuaranteeCalculation(transitOperationId, exchangeRate, isFetchCalculationEnabled)

  const calculationResult = data || EMPTY_GUARANTEE_CALCULATION

  const resolveAndClose = () => {
    onResolve(calculationResult.calculatedTaxesSum)
    onClose()
  }

  const isLoading = isExchangeRateLoading || isGuaranteeCalculationLoading

  const isResolveDisabled = isLoading || !data || calculationResult.calculatedTaxesSum <= 0

  return (
    <Modal
      show={isOpen}
      size="xl"
      aria-labelledby="contained-modal-title-vcenter"
      onHide={onClose}
      animation={false}
      backdropClassName="backdrop-with-modal"
      centered
      dialogClassName="guarantee-calculation-modal-dialog"
      fullscreen="lg-down"
    >
      <Modal.Header>
        <Modal.Title id="guarantee-calculation-modal-title">
          <h2 className="text-capitalize">
            {t('guaranteeCalculator.preview')}
          </h2>

        </Modal.Title>
        <button
          type="button"
          className="btn btn-link no-underline btn-lg d-flex justify-content-center align-items-center px-0"
          onClick={onClose}
        >
          <span>{t('buttons.close')}</span>
          <i className="fal fa-times fa-2x ms-2" />
        </button>
      </Modal.Header>
      <Modal.Body className="guarantee-calculation-modal-body">
        {
          isLoading && (
            <div>
              <LoadingBackdrop loading={isLoading} isInTable={false} />
            </div>
          )
        }
        {!isLoading && (
          <>
            <div className="guarantee-calculation-container guarantee-calculation-header">
              <div />
              <div>{t('guaranteeCalculator.price')}</div>
              <div>{t('guaranteeCalculator.excise')}</div>
              <div>{t('guaranteeCalculator.dutyRate')}</div>
              <div>{t('guaranteeCalculator.taxableDutyAmount')}</div>
              <div>{t('guaranteeCalculator.vatRate')}</div>
              <div>{t('guaranteeCalculator.taxableVatAmount')}</div>
              <div>{t('guaranteeCalculator.taxableItemPrice')}</div>
            </div>
            <div className="guarantee-calculation-scrollable">
              <div className="guarantee-calculation-container">
                {
                  calculationResult.rows.map((
                    rowItem,
                  ) => <GuaranteeCalculationRow key={`calc_row_${rowItem.sequenceNumber}`} rowItem={rowItem} />)
                }
              </div>
            </div>
            <div className="guarantee-calculation-summary d-flex justify-content-end px-3">
              <div>
                <div className="mr-2">{t('guaranteeCalculator.totalPrice')}</div>
                {
                  exchangeRate > 0 && (
                    <div className="d-flex justify-content-end">
                      {`1 EUR = ${calculationResult.currencyExchangeRate}`}
                    </div>
                  )
                }
                <div className="d-flex justify-content-end">
                  {`${calculationResult.calculatedTaxesSum.toFixed(3)} EUR`}
                </div>
                {
                  exchangeRate > 0 && (
                    <div className="d-flex justify-content-end">
                      {`${calculationResult.foreignCurrencyCalculatedSum} ${calculationResult.foreignCurrency}`}
                    </div>
                  )
                }
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          disabled={isResolveDisabled}
          className="btn btn-dark btn-lg m-0 text-primary"
          onClick={resolveAndClose}
        >
          {isLoading ? <LoadingSpinner /> : <span>{t('guaranteeCalculator.useResultValue')}</span>}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default GuaranteeCalculationModal
